import { ColDef, ColumnGroup } from "@ag-grid-community/core";
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAgGridEventState } from "shared/components/table/ag/AgGridEventState";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import {
  flattenColDefs,
  isColDef,
  tryGetAgGridApi,
} from "shared/components/table/maybern/MaybernTableUtils";
import { useElementRect } from "shared/hooks/useElementRect";
import { isTruthy } from "shared/utils/utils";
import { useWindowSize } from "usehooks-ts";

export interface MaybernTableColumnsPopoverProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const MaybernTableColumnsPopover: React.FC<
  React.PropsWithChildren<MaybernTableColumnsPopoverProps>
> = ({ isOpen, onOpen, onClose, children }) => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();

  const { height: windowHeight } = useWindowSize();

  const triggerRef = useRef<HTMLDivElement>(null);

  const { top: triggerTop, height: triggerHeight } = useElementRect(triggerRef);

  const isBelow = windowHeight - triggerTop - triggerHeight > 200;

  const maxHeight =
    windowHeight && triggerTop
      ? isBelow
        ? windowHeight - triggerTop - triggerHeight - 48
        : triggerTop - 48
      : undefined;

  const columnDefs =
    useAgGridEventState(agGrid, "columnEverythingChanged", (api) =>
      flattenColDefs(api.getColumnDefs())
        ?.filter(isColDef)
        .filter((col) => !!col.headerName),
    ) ?? [];

  const setAllColumnsVisibility = (visible: boolean) => {
    const keys = columnDefs.map((colDef) => colDef.colId).filter(isTruthy);
    if (keys.length) {
      tryGetAgGridApi(agGrid)?.setColumnsVisible(keys, visible);
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      offset={[0, 6]}
      isLazy={true}
      lazyBehavior="unmount"
    >
      <PopoverTrigger>
        <Box ref={triggerRef}>{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent maxWidth="366px" mx="16px">
          <PopoverArrow />
          <PopoverBody
            maxHeight={maxHeight}
            overflow="hidden"
            p={0}
            flex={1}
            minHeight={0}
            display="flex"
            flexDirection="column"
            alignItems="stretch"
          >
            {maxHeight !== undefined && (
              <>
                <VStack
                  flex="1"
                  px="24px"
                  py="24px"
                  spacing="16px"
                  alignItems="flex-start"
                  overflowY="auto"
                >
                  {columnDefs.map((colDef) => (
                    <ColumnListItem key={colDef.colId} colDef={colDef} />
                  ))}
                </VStack>
                <HStack px="24px" py="16px" gap="16px" borderTopWidth="1px">
                  <Button
                    colorScheme="secondary"
                    size="sm"
                    flex="1"
                    onClick={() => setAllColumnsVisibility(true)}
                  >
                    {t("common:table.showAll")}
                  </Button>
                  <Button
                    colorScheme="secondary"
                    size="sm"
                    flex="1"
                    onClick={() => setAllColumnsVisibility(false)}
                  >
                    {t("common:table.hideAll")}
                  </Button>
                </HStack>
              </>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const ColumnListItem: React.FC<{
  colDef: ColDef;
}> = ({ colDef }) => {
  const { agGrid } = useMaybernTableUIContext();

  const columnId = colDef.colId!;

  const parents = useAgGridEventState(
    agGrid,
    "columnEverythingChanged",
    (api) => {
      const getParent = (
        col: ColumnGroup | undefined | null,
      ): ColumnGroup[] => {
        if (!col) {
          return [];
        }
        const parent = col.getParent() as ColumnGroup | null;
        if (parent) {
          return [...getParent(parent), col];
        }
        return [col];
      };
      return getParent(
        api.getColumn(columnId)?.getParent() as ColumnGroup | null,
      );
    },
  );

  const parentNames = parents
    ?.map((p) => p.getProvidedColumnGroup().getColGroupDef()?.headerName)
    .filter(Boolean);

  const isChecked = useAgGridEventState(
    agGrid,
    "columnEverythingChanged",
    (api) => api.getColumn(columnId)?.isVisible(),
  );

  const isDisabled = useAgGridEventState(
    agGrid,
    "columnEverythingChanged",
    (api) => api.getColumn(columnId)?.getColDef().lockVisible,
  );

  return (
    <HStack spacing="16px">
      <Switch
        size="lg"
        isChecked={isChecked}
        isDisabled={isDisabled}
        onChange={(event) => {
          const api = tryGetAgGridApi(agGrid);
          api?.setColumnsVisible([columnId], event.currentTarget.checked);
          api?.autoSizeAllColumns();
        }}
      />
      <Text>
        {parentNames ? (
          <Text as="span" color="gray.500">
            {[...parentNames, ""].join(" > ")}
          </Text>
        ) : null}
        {colDef.headerName}
      </Text>
    </HStack>
  );
};
